/**
 * Maps hand-off (CONTEXT.md): open a business in the user's maps app.
 *
 * Android: a geo: intent, so the OS chooser and default app apply.
 * Web: Google Maps. iOS: Apple Maps, or a sheet listing Apple Maps plus
 * whichever of Google Maps / Waze is installed (detection needs
 * LSApplicationQueriesSchemes in app.config.ts).
 */

import { ActionSheetIOS, Linking, Platform } from 'react-native';
import { createLogger } from '@/utils/logger';
import {
  androidGeoUrl,
  appleMapsUrl,
  googleMapsAppUrl,
  googleMapsWebUrl,
  wazeUrl,
  type MapsPlace,
} from '@/utils/maps';

const logger = createLogger('MAPS');

interface MapsApp {
  label: string;
  /** Scheme probed with canOpenURL. */
  probe: string;
  build: (place: MapsPlace) => string | null;
}

const IOS_ALTERNATIVES: readonly MapsApp[] = [
  { label: 'Google Maps', probe: 'comgooglemaps://', build: googleMapsAppUrl },
  { label: 'Waze', probe: 'waze://', build: wazeUrl },
];

interface MapsChoice {
  label: string;
  url: string;
}

async function open(url: string | null): Promise<void> {
  if (!url) return;
  try {
    await Linking.openURL(url);
  } catch (error) {
    logger.warn('Maps hand-off failed to open', { error });
  }
}

async function isInstalled(probe: string): Promise<boolean> {
  try {
    return await Linking.canOpenURL(probe);
  } catch {
    return false;
  }
}

async function installedAlternatives(place: MapsPlace): Promise<MapsChoice[]> {
  const checks = await Promise.all(
    IOS_ALTERNATIVES.map(async (app) => ({ app, installed: await isInstalled(app.probe) })),
  );
  return checks
    .filter(({ installed }) => installed)
    .map(({ app }) => ({ label: app.label, url: app.build(place) }))
    .filter((choice): choice is MapsChoice => choice.url !== null);
}

async function openOnIos(place: MapsPlace): Promise<void> {
  const apple = appleMapsUrl(place);
  if (!apple) return;

  const alternatives = await installedAlternatives(place);
  if (alternatives.length === 0) {
    await open(apple);
    return;
  }

  const choices: MapsChoice[] = [{ label: 'Apple Maps', url: apple }, ...alternatives];
  ActionSheetIOS.showActionSheetWithOptions(
    {
      title: place.name,
      options: [...choices.map((choice) => choice.label), 'Cancel'],
      cancelButtonIndex: choices.length,
    },
    (index) => {
      if (index < choices.length) {
        void open(choices[index].url);
      }
    },
  );
}

export async function openMapsHandoff(
  place: MapsPlace,
  platform: typeof Platform.OS = Platform.OS,
): Promise<void> {
  if (platform === 'ios') return openOnIos(place);
  if (platform === 'android') return open(androidGeoUrl(place));
  return open(googleMapsWebUrl(place));
}
